import { SITE_ANNOUNCEMENTS } from '@/constants/siteAnnouncements'
import { ORG_TYPE_FACILITY, ORG_TYPE_VISIT, PORTAL_FAQS, PORTAL_SHORTCUTS } from '@/constants/portalHome'
import { getAllProducts } from '@/constants/mallProducts'
import { NOTICE_PUBLIC_PATH } from '@/constants/noticeRoutes'
import { MALL_HOME_PATH, mallProductPath } from '@/constants/mall'

export type SearchResultCategory = '서비스' | '공지사항' | '함우리몰' | 'FAQ' | '상담'

export interface SearchIndexItem {
  id: string
  category: SearchResultCategory
  title: string
  description: string
  path: string
  breadcrumb: string[]
  keywords: string
}

interface PortalSearchDocs {
  announcements: Array<{
    id: string
    title: string
    summary: string
    content: string
    category: string
  }>
  newsletters: Array<{
    id: string
    title: string
    summary: string
    content: string
    tags?: string[]
  }>
  faqs: Array<{ idx?: number; q: string; a: string }>
}

let portalDocs: PortalSearchDocs | null = null

/** 관리자 CMS에서 불러온 공지·FAQ로 검색 색인을 갱신한다. */
export function setPortalSearchDocs(docs: PortalSearchDocs | null) {
  portalDocs = docs
}

const STATIC_INDEX: SearchIndexItem[] = [
  {
    id: 'svc-wage',
    category: '서비스',
    title: '임금관리 서비스',
    description: '인건비 절감, 통상임금 리스크 점검, 임금대장·4대보험·세무 신고',
    path: '/wage',
    breadcrumb: ['홈', '서비스', '임금관리'],
    keywords: '임금 급여 임금관리 임금서비스 통상임금 4대보험 명세서',
  },
  {
    id: 'svc-accounting',
    category: '서비스',
    title: '회계관리 서비스',
    description: '장기요양기관 회계규칙 준수, 장부·예결산·감사 대비',
    path: '/accounting',
    breadcrumb: ['홈', '서비스', '회계관리'],
    keywords: '회계 장부 예결산 결산 감사',
  },
  {
    id: 'svc-facility',
    category: '서비스',
    title: ORG_TYPE_FACILITY,
    description: '시설 통합관리, 임금·회계·고시 연계',
    path: '/facility',
    breadcrumb: ['홈', '서비스', ORG_TYPE_FACILITY],
    keywords: '요양원 주간보호센터 요양시설 주야간보호 시설',
  },
  {
    id: 'svc-visit',
    category: '서비스',
    title: ORG_TYPE_VISIT,
    description: '청구·급여·신고 연계 전용 프로그램',
    path: '/visiting-care',
    breadcrumb: ['홈', '서비스', ORG_TYPE_VISIT],
    keywords: '방문요양 방문목욕 재가',
  },
  {
    id: 'svc-mall',
    category: '함우리몰',
    title: '함우리몰',
    description: '장기요양기관 전용 쇼핑몰',
    path: MALL_HOME_PATH,
    breadcrumb: ['홈', '함우리몰'],
    keywords: '함우리몰 쇼핑몰 기관단가 위생용품',
  },
  {
    id: 'svc-newsletter',
    category: '공지사항',
    title: '뉴스레터',
    description: '운영 소식 뉴스레터',
    path: '/newsletters',
    breadcrumb: ['홈', '뉴스레터'],
    keywords: '뉴스레터 소식',
  },
  {
    id: 'svc-consult',
    category: '상담',
    title: '상담 신청',
    description: '임금·회계·기관 운영 상담 신청',
    path: '/consult-board/write',
    breadcrumb: ['홈', '상담'],
    keywords: '상담 신청 내역',
  },
  {
    id: 'svc-notice',
    category: '공지사항',
    title: '고시 · 공지',
    description: '장기요양 고시·공지 안내',
    path: NOTICE_PUBLIC_PATH,
    breadcrumb: ['홈', '게시판'],
    keywords: '고시 공지 법령',
  },
]

function buildIndex(): SearchIndexItem[] {
  const items: SearchIndexItem[] = [...STATIC_INDEX]

  PORTAL_SHORTCUTS.forEach((s, i) => {
    items.push({
      id: `shortcut-${i}`,
      category: '서비스',
      title: s.label,
      description: s.desc || s.label,
      path: s.to,
      breadcrumb: ['홈', s.label],
      keywords: `${s.label} ${s.desc || ''}`,
    })
  })

  const announcements = portalDocs?.announcements?.length
    ? portalDocs.announcements
    : SITE_ANNOUNCEMENTS.map((a) => ({
        id: a.id,
        title: a.title,
        summary: a.summary,
        content: a.content,
        category: a.category,
      }))

  announcements.forEach((a) => {
    items.push({
      id: `ann-${a.id}`,
      category: '공지사항',
      title: a.title,
      description: a.summary,
      path: `/announcements/${a.id}`,
      breadcrumb: ['홈', '공지사항', a.title],
      keywords: `${a.title} ${a.summary} ${a.content} ${a.category}`,
    })
  })

  ;(portalDocs?.newsletters || []).forEach((item) => {
    const plain = item.content.replace(/<[^>]+>/g, ' ')
    items.push({
      id: `nl-${item.id}`,
      category: '공지사항',
      title: item.title,
      description: item.summary,
      path: `/newsletters/${item.id}`,
      breadcrumb: ['홈', '뉴스레터', item.title],
      keywords: `${item.title} ${item.summary} ${plain} ${(item.tags || []).join(' ')} 뉴스레터`,
    })
  })

  getAllProducts().forEach((p) => {
    items.push({
      id: `mall-${p.id}`,
      category: '함우리몰',
      title: p.name,
      description: p.summary,
      path: mallProductPath(p.id),
      breadcrumb: ['홈', '함우리몰', p.name],
      keywords: `${p.name} ${p.summary} ${(p.tags || []).join(' ')} 함우리몰`,
    })
  })

  const faqs = portalDocs?.faqs?.length
    ? portalDocs.faqs
    : PORTAL_FAQS.map((f, i) => ({ idx: i + 1, q: f.q, a: f.a }))

  faqs.forEach((f, i) => {
    items.push({
      id: `faq-${f.idx ?? i}`,
      category: 'FAQ',
      title: f.q,
      description: f.a,
      path: '/faq',
      breadcrumb: ['홈', '게시판', 'FAQ', f.q],
      keywords: `${f.q} ${f.a}`,
    })
  })

  return items
}

let cachedIndex: SearchIndexItem[] | null = null

export function getSearchIndex(): SearchIndexItem[] {
  if (!cachedIndex) cachedIndex = buildIndex()
  return cachedIndex
}

/** 몰 상품 목록을 새로 받아 오면 색인도 다시 만든다. */
export function resetSearchIndex() {
  cachedIndex = null
}

export function searchSite(query: string, limit = 50): SearchIndexItem[] {
  const term = query.trim().toLowerCase()
  if (!term) return []

  const index = getSearchIndex()
  const scored = index
    .map((item) => {
      const title = item.title.toLowerCase()
      const desc = item.description.toLowerCase()
      const kw = item.keywords.toLowerCase()
      let score = 0
      if (title.includes(term)) score += 10
      if (title === term) score += 5
      if (desc.includes(term)) score += 5
      if (kw.includes(term)) score += 3
      if (item.category === '서비스' && term.includes('임금') && kw.includes('임금')) score += 4
      return { item, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)

  return scored.slice(0, limit).map(({ item }) => item)
}

export function searchSiteSuggest(
  query: string,
  limitPerCategory = 3,
): Record<SearchResultCategory, SearchIndexItem[]> {
  const results = searchSite(query, 30)
  const grouped: Record<SearchResultCategory, SearchIndexItem[]> = {
    서비스: [],
    공지사항: [],
    함우리몰: [],
    FAQ: [],
    상담: [],
  }

  for (const item of results) {
    const list = grouped[item.category]
    if (list.length < limitPerCategory) list.push(item)
  }

  return grouped
}
